import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type MoveDocumentToFolderParams = {
  documentId: string
  folderId?: number
}

const moveDocumentToFolder = async (
  apiClient: AxiosInstance,
  params: MoveDocumentToFolderParams
) => {
  const { data } = await apiClient.postForm(`/ajax/folders/add/crunches`, {
    folder_id: params.folderId,
    crunches_uuid_list: [params.documentId],
  })

  return data.data
}

export const useMoveDocumentToFolder = (params: { onSuccess: () => void }) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation(
    (mutationParams: MoveDocumentToFolderParams) =>
      moveDocumentToFolder(apiClient, mutationParams),
    {
      onSuccess: async (_, context: MoveDocumentToFolderParams) => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) => {
            return shouldInvalidateCrunchesPage(page, context.documentId)
          },
        })

        toast({
          variant: 'success',
          title: 'Successfully moved document',
        })

        params?.onSuccess()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error moving document',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
